import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Flex, Typography } from '@iheartjane/reefer';
import type {
  FontFamily,
  GoogleFontFamily,
  MenuComponentConfig,
  Nullable,
} from '@iheartjane/types';
import { useCallback, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { FontPicker } from '@jane/business-admin/components/premium/FontPicker';
import type { FontPickerProps } from '@jane/business-admin/components/premium/FontPicker';
import {
  isCustomFontFamily,
  toLabeledFontOption,
} from '@jane/business-admin/util';
import { ConfirmChangeModal } from '@jane/shared-b2b/components';

const NO_FONTS: FontFamily[] = [];
const FONT_SLOTS = ['heading', 'body'] as const;
type FontSlot = (typeof FONT_SLOTS)[number];

const SLOT_LABELS: Record<FontSlot, string> = {
  heading: 'Headings',
  body: 'Paragraph',
};

const removalWarning = (slots: FontSlot[]) =>
  `This font will be removed from ${slots
    .map((slot) => SLOT_LABELS[slot])
    .join(
      ' and '
    )}, and your menu will switch back to the default font. You can re-import it anytime with the font URL.`;

export const FontsSection = ({
  supportedFontFamilies,
}: {
  supportedFontFamilies: GoogleFontFamily[];
}) => {
  const customFontsFlag = useFlag(FLAGS.c2CustomFontImport);
  const [fontToRemove, setFontToRemove] = useState<
    Nullable<{
      font: FontFamily;
      slots: FontSlot[];
    }>
  >(null);
  const { getValues, setValue } = useFormContext<MenuComponentConfig>();
  const customFontFamilies =
    useWatch<MenuComponentConfig, 'fonts.custom'>({ name: 'fonts.custom' }) ??
    NO_FONTS;

  const rememberImport = useCallback(
    (font: Nullable<FontFamily>) => {
      if (!isCustomFontFamily(font)) {
        return;
      }

      const library = getValues('fonts.custom') ?? [];

      if (library.some(({ font_family }) => font_family === font.font_family)) {
        return;
      }

      setValue('fonts.custom', [...library, font], { shouldDirty: true });
    },
    [getValues, setValue]
  );

  const slotsUsing = useCallback(
    (font: FontFamily) =>
      FONT_SLOTS.filter(
        (slot) => getValues(`fonts.${slot}`)?.font_family === font.font_family
      ),
    [getValues]
  );

  const forgetImport = useCallback(
    (font: FontFamily) => {
      setValue(
        'fonts.custom',
        (getValues('fonts.custom') ?? []).filter(
          ({ font_family }) => font_family !== font.font_family
        ),
        { shouldDirty: true }
      );

      slotsUsing(font).forEach((slot) =>
        setValue(`fonts.${slot}`, null, { shouldDirty: true })
      );
    },
    [getValues, setValue, slotsUsing]
  );

  const requestRemoval = useCallback(
    (font: FontFamily) => {
      const slots = slotsUsing(font);

      if (slots.length) {
        setFontToRemove({ font, slots });
      } else {
        forgetImport(font);
      }
    },
    [forgetImport, slotsUsing]
  );

  const pickerProps: Pick<
    FontPickerProps,
    | 'customFontFamilies'
    | 'enableCustomFonts'
    | 'fontFamilies'
    | 'onRemoveCustomFont'
  > = {
    customFontFamilies: customFontsFlag ? customFontFamilies : NO_FONTS,
    enableCustomFonts: customFontsFlag,
    fontFamilies: supportedFontFamilies,
    onRemoveCustomFont: requestRemoval,
  };

  return (
    <Flex flexDirection="column" gap={24}>
      <Typography>
        Menus use a default font until you select or upload your own brand font.
      </Typography>

      <Flex flexDirection="column" gap={8}>
        <Typography variant="body-semibold">Headings</Typography>

        <Controller
          name="fonts.heading"
          render={({ field }) => (
            <FontPicker
              {...pickerProps}
              activeFontFamily={toLabeledFontOption(field.value)}
              defaultFontFamily={{
                label: 'Euclid Circular B',
                value: 'Euclid Circular B',
              }}
              label="heading-font"
              onChange={(font) => {
                field.onChange(font);
                rememberImport(font);
              }}
            />
          )}
        />
      </Flex>

      <Flex flexDirection="column" gap={8}>
        <Typography variant="body-semibold">Paragraph</Typography>
        <Controller
          name="fonts.body"
          render={({ field }) => (
            <FontPicker
              {...pickerProps}
              activeFontFamily={toLabeledFontOption(field.value)}
              defaultFontFamily={{
                label: 'Source Sans Pro',
                value: 'Jane Default',
              }}
              label="body-font"
              onChange={(font) => {
                field.onChange(font);
                rememberImport(font);
              }}
            />
          )}
        />
      </Flex>

      {fontToRemove && (
        <ConfirmChangeModal
          open
          title={`Remove ${fontToRemove.font.font_family}?`}
          subtitle={removalWarning(fontToRemove.slots)}
          cancelLabel="Cancel"
          confirmLabel="Remove"
          confirmButtonVariant="destructive"
          onCancel={() => setFontToRemove(null)}
          onConfirm={() => {
            forgetImport(fontToRemove.font);
            setFontToRemove(null);
          }}
        />
      )}
    </Flex>
  );
};
